import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs'
import { useUrlSearchText } from '@/hooks/use-url-search-text'
import { Link } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Search, MessageSquare, AlertCircle, Clock, CheckCircle, Calendar } from 'lucide-react'
import { useCustomerCommThreads, useCustomerCommStats } from '@/api/hooks'
import { formatFollowUpTime, formatStatus, parseUTCDate, resultCount } from '@/lib/utils'
import { SearchInput } from '@/components/search-input'
import { ListFilter, ListFilters } from '@/components/list-filters'

// Temporary types until OpenAPI schema is regenerated
interface CustomerCommThreadItem {
  id: number
  subject: string
  status: string
  channel: string
  recipient_name: string | null
  recipient_email: string | null
  last_activity_at: string | null
  follow_up_at: string | null
  follow_up_count: number
  max_follow_ups: number
  notification_type: string | null
  auto_send: boolean | null
  actions: { id: number }[]
}

interface PaginatedThreads {
  items: CustomerCommThreadItem[]
  total: number
  page: number
  pages: number
}

interface CustomerCommStats {
  total_threads: number
  needs_attention: number
  awaiting_reply: number
  open_actions: number
}

const STATUS_OPTIONS = ['all', 'needs_attention', 'awaiting_reply', 'replied', 'resolved', 'draft']
const CHANNEL_OPTIONS = ['all', 'email', 'sms', 'shopify']

function statusBadgeVariant(
  status: string,
): 'destructive' | 'default' | 'secondary' | 'outline' {
  switch (status) {
    case 'needs_attention':
      return 'destructive'
    case 'awaiting_reply':
      return 'default'
    case 'replied':
      return 'secondary'
    case 'resolved':
      return 'outline'
    case 'draft':
      return 'secondary'
    default:
      return 'secondary'
  }
}

function channelBadgeVariant(channel: string): 'default' | 'secondary' | 'outline' {
  switch (channel) {
    case 'email':
      return 'default'
    case 'sms':
      return 'secondary'
    case 'shopify':
      return 'outline'
    default:
      return 'outline'
  }
}

function notificationTypeColor(notificationType: string): string {
  const colors: Record<string, string> = {
    order_confirmation: 'bg-green-100 text-green-800',
    po_sent: 'bg-blue-100 text-blue-800',
    manufacturer_confirmed: 'bg-green-100 text-green-800',
    shipment_booked: 'bg-blue-100 text-blue-800',
    shipped: 'bg-indigo-100 text-indigo-800',
    delivery_reminder: 'bg-indigo-100 text-indigo-800',
    delivery_check_in: 'bg-teal-100 text-teal-800',
    delivered: 'bg-green-100 text-green-800',
    delayed: 'bg-amber-100 text-amber-800',
    exception: 'bg-red-100 text-red-800',
    booking_failed: 'bg-amber-100 text-amber-800',
    backorder: 'bg-amber-100 text-amber-800',
    substitution_available: 'bg-purple-100 text-purple-800',
    price_change: 'bg-orange-100 text-orange-800',
  }
  return colors[notificationType] || 'bg-gray-100 text-gray-800'
}

function FollowUpBadge({ followUpAt, count, max }: { followUpAt: string; count: number; max: number }) {
  const today = new Date().toISOString().slice(0, 10)
  const isToday = followUpAt === today
  const isPast = followUpAt < today
  const variant = isPast ? 'destructive' : isToday ? 'default' : 'secondary'
  return (
    <Badge variant={variant} className="text-xs">
      <Calendar className="mr-1 h-3 w-3" />
      {formatFollowUpTime(followUpAt)} ({count}/{max})
    </Badge>
  )
}

export function CustomerCommunicationsPage() {
  const [search, setSearch] = useUrlSearchText()
  const [{ status, channel, page }, setFilters] = useQueryStates({
    status: parseAsString.withDefault('all'),
    channel: parseAsString.withDefault('all'),
    page: parseAsInteger.withDefault(1),
  })

  const { data: rawData, isLoading } = useCustomerCommThreads({
    search: search || undefined,
    status: status === 'all' ? undefined : status,
    channel: channel === 'all' ? undefined : channel,
    page,
    limit: 20,
  })
  const data = rawData as PaginatedThreads | undefined

  const { data: rawStats } = useCustomerCommStats()
  const stats = rawStats as CustomerCommStats | undefined

  const statCards = [
    {
      label: 'Total Threads',
      value: stats?.total_threads ?? 0,
      icon: MessageSquare,
    },
    {
      label: 'Needs Attention',
      value: stats?.needs_attention ?? 0,
      icon: AlertCircle,
    },
    {
      label: 'Awaiting Reply',
      value: stats?.awaiting_reply ?? 0,
      icon: Clock,
    },
    {
      label: 'Open Actions',
      value: stats?.open_actions ?? 0,
      icon: CheckCircle,
    },
  ]

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Customer Communications</h1>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((card) => (
          <Card key={card.label}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {card.label}
              </CardTitle>
              <card.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{card.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <ListFilters

        count={resultCount(data?.total, 'thread')}
        active={[status !== 'all', channel !== 'all'].filter(Boolean).length}
        onClear={() => void setFilters({ status: null, channel: null, page: 1 })}
        search={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <SearchInput
              placeholder="Search by customer, subject..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                void setFilters({ page: 1 })
              }}
              className="pl-9"
            />
          </div>
        }
      >
        <ListFilter label="Status">
          <Select
            value={status}
            onValueChange={(v) => {
              void setFilters({ status: v, page: 1 })
            }}
          >
            <SelectTrigger aria-label="Status filter" className="w-full">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>
                  {s === 'all' ? 'All Statuses' : formatStatus(s)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ListFilter>
        <ListFilter label="Channel">
          <Select
            value={channel}
            onValueChange={(v) => {
              void setFilters({ channel: v, page: 1 })
            }}
          >
            <SelectTrigger aria-label="Channel filter" className="w-full">
              <SelectValue placeholder="Channel" />
            </SelectTrigger>
            <SelectContent>
              {CHANNEL_OPTIONS.map((c) => (
                <SelectItem key={c} value={c}>
                  {c === 'all' ? 'All Channels' : formatStatus(c)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </ListFilter>
      </ListFilters>

      {/* Desktop table */}
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Subject</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Channel</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Follow-up</TableHead>
              <TableHead>Last Activity</TableHead>
              <TableHead className="text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : data?.items?.length ? (
              data.items.map((thread) => (
                <TableRow key={thread.id}>
                  <TableCell>
                    {thread.recipient_name || thread.recipient_email || '-'}
                  </TableCell>
                  <TableCell>
                    <Link
                      to={'/customer-comms/$threadId' as string}
                      params={{ threadId: String(thread.id) } as Record<string, string>}
                      className="font-medium text-primary hover:underline"
                    >
                      {thread.subject || '(No subject)'}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {thread.notification_type ? (
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${notificationTypeColor(thread.notification_type)}`}
                      >
                        {formatStatus(thread.notification_type)}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={channelBadgeVariant(thread.channel)}>
                      {formatStatus(thread.channel)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusBadgeVariant(thread.status)}>
                      {formatStatus(thread.status)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {thread.follow_up_at && (thread.status === 'sent' || thread.status === 'awaiting_reply') ? (
                      <FollowUpBadge followUpAt={thread.follow_up_at} count={thread.follow_up_count} max={thread.max_follow_ups} />
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {thread.last_activity_at
                      ? parseUTCDate(thread.last_activity_at).toLocaleDateString()
                      : '-'}
                  </TableCell>
                  <TableCell className="text-center">
                    {thread.actions && thread.actions.length > 0 ? (
                      <Badge variant="destructive">{thread.actions.length}</Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground">
                  No communication threads found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile cards */}
      <div className="space-y-2 md:hidden">
        {isLoading ? (
          <div className="rounded-lg border bg-card py-4 text-center text-sm text-muted-foreground">
            Loading...
          </div>
        ) : data?.items?.length ? (
          data.items.map((thread) => (
            <Link
              key={thread.id}
              to={'/customer-comms/$threadId' as string}
              params={{ threadId: String(thread.id) } as Record<string, string>}
              className="block rounded-lg border bg-card p-3 transition-colors hover:bg-muted/40"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="min-w-0 flex-1 break-words text-sm font-medium text-primary">
                  {thread.subject || '(No subject)'}
                </p>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <Badge variant={statusBadgeVariant(thread.status)}>
                    {formatStatus(thread.status)}
                  </Badge>
                  {thread.actions && thread.actions.length > 0 && (
                    <Badge variant="destructive" className="text-xs">
                      {thread.actions.length} action{thread.actions.length > 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
              </div>
              <p className="mt-1 break-all text-xs text-muted-foreground">
                {thread.recipient_name || thread.recipient_email || '-'}
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <Badge variant={channelBadgeVariant(thread.channel)} className="text-xs">
                  {formatStatus(thread.channel)}
                </Badge>
                {thread.notification_type && (
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${notificationTypeColor(thread.notification_type)}`}
                  >
                    {formatStatus(thread.notification_type)}
                  </span>
                )}
                {thread.follow_up_at &&
                  (thread.status === 'sent' || thread.status === 'awaiting_reply') && (
                    <FollowUpBadge
                      followUpAt={thread.follow_up_at}
                      count={thread.follow_up_count}
                      max={thread.max_follow_ups}
                    />
                  )}
                {thread.last_activity_at && (
                  <span className="text-xs text-muted-foreground">
                    {parseUTCDate(thread.last_activity_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </Link>
          ))
        ) : (
          <div className="rounded-lg border bg-card py-4 text-center text-sm text-muted-foreground">
            No communication threads found
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3">
        <p className="text-sm text-muted-foreground">
          Page {data?.page ?? 1} of {data?.pages ?? 1} ({data?.total ?? 0} total)
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void setFilters({ page: Math.max(1, page - 1) })}
            disabled={page <= 1}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void setFilters({ page: page + 1 })}
            disabled={page >= (data?.pages ?? 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}
